import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { Account } from './api/client.js'
import { api, tokens } from './api/client.js'
import { Home } from './routes/Home.js'
import { Player } from './routes/Player.js'

const Studio = lazy(() => import('./routes/Studio.js').then((m) => ({ default: m.Studio })))
const Ops = lazy(() => import('./routes/Ops.js').then((m) => ({ default: m.Ops })))
const Checkout = lazy(() => import('./routes/Checkout.js').then((m) => ({ default: m.Checkout })))

interface AuthValue {
  account: Account | undefined
  loading: boolean
  setAccount: (account: Account | undefined) => void
  signOut: () => void
}

const AuthContext = createContext<AuthValue>({
  account: undefined,
  loading: true,
  setAccount: () => undefined,
  signOut: () => undefined,
})

export const useAuth = (): AuthValue => useContext(AuthContext)

export function App(): ReactNode {
  const [account, setAccount] = useState<Account | undefined>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tokens.access) {
      setLoading(false)
      return
    }
    api
      .me()
      .then((me) => setAccount(me))
      .catch(() => tokens.clear())
      .finally(() => setLoading(false))
  }, [])

  const signOut = useCallback(() => {
    api.logOut()
    setAccount(undefined)
  }, [])

  return (
    <AuthContext.Provider value={{ account, loading, setAccount, signOut }}>
      <div className="app">
        <TopBar />
        <Suspense fallback={<main className="empty">Loading…</main>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/studio/:worldId" element={<Guarded><Studio /></Guarded>} />
          <Route path="/play/:worldId" element={<Player />} />
          <Route path="/ops/:worldId" element={<Guarded><Ops /></Guarded>} />
          <Route path="/checkout/:checkoutId" element={<Guarded><Checkout /></Guarded>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </AuthContext.Provider>
  )
}

function TopBar(): ReactNode {
  const { account, signOut } = useAuth()
  const location = useLocation()
  // The player takes over the screen; a navigation bar above it is one more
  // thing to knock while walking.
  if (location.pathname.startsWith('/play/')) return null

  return (
    <header className="topbar">
      <Link to="/" className="brand">
        Sound<span>World</span>
      </Link>
      <div className="spacer" />
      <nav>
        {account ? (
          <>
            <span className="faint">{account.displayName}</span>
            {account.guest ? <span className="pill">guest</span> : null}
            <button className="ghost" onClick={signOut}>
              Sign out
            </button>
          </>
        ) : null}
      </nav>
    </header>
  )
}

function Guarded({ children }: { children: ReactNode }): ReactNode {
  const { account, loading } = useAuth()
  if (loading) return <main className="empty">Loading…</main>
  if (!account) return <Navigate to="/" replace />
  return <>{children}</>
}
